"use client";

import React, {useCallback, useRef, useState} from "react";
import toast from "react-hot-toast";
import {AiFillCloseCircle, AiOutlinePaperClip} from "react-icons/ai";

const ALLOWED_MIME = new Set(["image/jpeg", "image/png", "application/pdf"]);
const ALLOWED_EXT = new Set(["jpg", "jpeg", "png", "pdf"]);

type Props = {
  value: any[];
  onChangeFile: (next: any[]) => void;
  max?: number;
  className?: string;
  variant?: "default" | "fuel" | "maintenance" | "miscellaneous";
  onError?: (msg: string) => void;
};

export function UploadFormField({
  value,
  onChangeFile,
  max = 3,
  className = "grid-cols-2",
  variant = "default",
  onError
}: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isCardVariant =
    variant === "fuel" ||
    variant === "maintenance" ||
    variant === "miscellaneous";
  const uploadCopy = {
    fuel: {
      description:
        "Add fuel receipts, invoices, or scanned PDFs. Files appear in the review panel immediately.",
      reviewDescription: "Review attached receipts before saving the entry.",
      emptyTitle: "No receipts added yet"
    },
    maintenance: {
      description:
        "Add maintenance bills, invoices, job cards, or scanned PDFs. Files appear in the review panel immediately.",
      reviewDescription:
        "Review attached maintenance documents before saving the entry.",
      emptyTitle: "No maintenance documents added yet"
    },
    miscellaneous: {
      description:
        "Add bills, receipts, invoices, or scanned PDFs. Files appear in the review panel immediately.",
      reviewDescription:
        "Review attached miscellaneous expense documents before saving the entry.",
      emptyTitle: "No miscellaneous documents added yet"
    },
    default: {
      description: "",
      reviewDescription: "",
      emptyTitle: ""
    }
  }[variant];

  const validateFile = useCallback((file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const okMime = ALLOWED_MIME.has(file.type);
    const okExt = ALLOWED_EXT.has(ext);
    return okMime || okExt;
  }, []);

  const addFiles = useCallback(
    (list: FileList | null) => {
      if (!list) return;
      const files = Array.from(list);
      const rejected = files.filter(f => !validateFile(f));
      if (rejected.length) {
        onError?.("Invalid files");
        toast.error(`Invalid files`);
        return;
      }

      const remaining = Math.max(0, max - value.length);
      if (list.length > remaining) {
        onError?.(`Maximum ${max} files can be uploaded`);
        toast.error(`Maximum ${max} files can be uploaded`);
        return;
      }

      const now = Date.now();
      const incoming: any[] = Array.from(list)
        .slice(0, remaining)
        .map((f, i) => ({
          id: `new-${now}-${i}`,
          name: f.name,
          sizeKB: Math.max(1, Math.round(f.size / 1024)),
          source: "new" as const,
          file: f
        }));

      if (incoming.length) onChangeFile([...value, ...incoming].slice(0, max));
    },
    [max, onChangeFile, onError, validateFile, value]
  );

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeItem = (id: string) => {
    onChangeFile(value.filter(x => x.id !== id));
  };

  return (
    <div className={`grid items-stretch gap-4 ${className}`}>
      <div
        className={`flex flex-col ${isCardVariant ? "rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_18px_40px_-28px_rgba(15,23,42,0.18)]" : ""}`}
      >
        <div className={isCardVariant ? "mb-3 flex flex-col gap-1" : ""}>
          <h4 className="text-sm font-semibold text-gray-900">
            Document Upload
          </h4>
          <small className={isCardVariant ? "text-sm text-slate-500" : "mb-2"}>
            Only PDF or images are allowed.
          </small>
        </div>
        <label
          onDragOver={e => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          className={[
            isCardVariant
              ? "group flex min-h-[180px] cursor-pointer flex-col items-center justify-center rounded-[20px] border-2 border-dashed px-5 py-6 text-center transition"
              : "flex h-40 cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed bg-white",
            isDragging
              ? isCardVariant
                ? "border-emerald-300 bg-emerald-50/80"
                : "border-green-400"
              : isCardVariant
                ? "border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_55%,#f0fdf4_100%)] hover:border-emerald-200 hover:bg-emerald-50/50"
                : "border-gray-300"
          ].join(" ")}
        >
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            multiple
            onChange={e => {
              addFiles(e.target.files);
              e.currentTarget.value = "";
              if (inputRef.current) inputRef.current.value = "";
            }}
            accept=".jpg,.jpeg,.png,.pdf"
          />
          <button
            type="button"
            className={`flex items-center justify-center rounded-full border ${
              isCardVariant
                ? "h-14 w-14 border-emerald-200 bg-emerald-100 text-emerald-700 shadow-sm"
                : "h-9 w-9 border-gray-200 bg-green-100"
            }`}
            onClick={() => inputRef.current?.click()}
            aria-label="Upload files"
          >
            <svg
              viewBox="0 0 24 24"
              className={isCardVariant ? "h-5 w-5" : "h-4 w-4"}
              fill="none"
              stroke="currentColor"
            >
              <path strokeWidth="1.5" d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" />
            </svg>
          </button>
          <p className={`mt-3 ${isCardVariant ? "text-base" : "text-sm"}`}>
            <span
              className={`font-semibold ${isCardVariant ? "text-emerald-600" : "text-green-600"}`}
              onClick={() => inputRef.current?.click()}
            >
              Click to Upload
            </span>{" "}
            <span className="text-gray-500">or drag and drop</span>
          </p>
          {isCardVariant && (
            <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
              {uploadCopy.description}
            </p>
          )}
        </label>
        <p
          className={`mt-3 text-xs ${isCardVariant ? "text-slate-500" : "text-[#3B82F6]"}`}
        >
          *Maximum {max} files can be uploaded.
        </p>
      </div>
      <div
        className={`flex flex-col ${
          isCardVariant
            ? "rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_18px_40px_-28px_rgba(15,23,42,0.18)]"
            : className?.includes("grid-cols-1")
              ? "mt-1"
              : "mt-6"
        }`}
      >
        {isCardVariant && (
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">
                Uploaded Files
              </h4>
              <p className="text-sm text-slate-500">
                {uploadCopy.reviewDescription}
              </p>
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {value.length}/{max}
            </span>
          </div>
        )}
        {value.length ? (
          value.map(f => (
            <div
              key={f.id}
              className={`mb-3 flex items-center justify-between ${
                isCardVariant
                  ? "rounded-[22px] border border-slate-200 bg-slate-50/70 px-4 py-3"
                  : "rounded-2xl border border-gray-200 bg-white px-4 py-2"
              }`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className={`flex flex-shrink-0 items-center justify-center rounded-full ${
                    isCardVariant ? "h-11 w-11 bg-amber-50" : "h-8 w-8"
                  }`}
                >
                  <AiOutlinePaperClip
                    className={`${
                      isCardVariant
                        ? "h-5 w-5 text-amber-500"
                        : "h-6 w-6 text-yellow-500"
                    }`}
                  />
                </div>
                <div className="min-w-0">
                  <p className="line-clamp-2 text-sm font-medium text-gray-900">
                    {f.name}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span>{f.sizeKB}kb</span>
                    {isCardVariant && (
                      <span className="rounded-full bg-white px-2 py-0.5 font-medium text-slate-500">
                        {f.source === "existing" ? "Stored file" : "New upload"}
                      </span>
                    )}
                  </div>
                  {/*f.source === "existing" && f.url && (
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 underline"
                    >
                      View / Download
                    </a>
                  )*/}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeItem(f.id)}
                className="rounded-full p-1 text-rose-500 transition hover:bg-rose-50"
                aria-label={`Remove ${f.name}`}
                title="Remove"
              >
                <AiFillCloseCircle className="h-6 w-6" />
              </button>
            </div>
          ))
        ) : isCardVariant ? (
          <div className="flex h-full min-h-[180px] flex-col items-center justify-center rounded-[20px] border border-dashed border-slate-200 bg-slate-50/70 px-5 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
              <AiOutlinePaperClip className="h-5 w-5 text-slate-400" />
            </div>
            <p className="text-sm font-semibold text-slate-700">
              {uploadCopy.emptyTitle}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
